import { useMemo } from 'react';
import { Skeleton } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { reportEvent } from '~/domains/analytics/events';
import { useFilterLeague } from '~/domains/contest/domains/lobby/components/Filters/useFilterLeague';
import { Text } from '~/domains/design-system/Text';
import { UnstyledButtonLink } from '~/domains/design-system/Button';
import { Routes } from '~/domains/common/constants/routes';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { SportTag } from '~/domains/common/enums/sport-tag.enum';
import repeat from '~/common/utils/repeat';

import { HomeSectionTitle } from './sectionComponents';
import useStyles from './HomeLeagueLobbies.styles';

function HomeLeagueLobbies({ order }: { order: number }) {
  const { classes, theme } = useStyles();
  const { t } = useTranslation('home');
  const { leagueFilterData, isLoadingLeagueFilterData } = useFilterLeague({ isDisabled: false });

  const LeagueCardsComponent = useMemo(() => {
    // @TODO: Polish the color matrix with design
    const SportColorMatrix = {
      [SportTag.GOLF]: ['#2B8A3E', theme.colors.green[4], theme.colors.red[6]],
      [SportTag.FOOTBALL]: [
        theme.colors.red[2],
        theme.colors.blue[3],
        theme.colors.gray[7],
        '#E88913',
      ],
      [SportTag.SOCCER]: [theme.colors.green[2], theme.colors.lime[5]],
      [SportTag.BASKETBALL]: [theme.colors.orange[2], theme.colors.pink[2], theme.colors.red[2]],
      [SportTag.BASEBALL]: [theme.colors.orange[3]],
      [SportTag.HOCKEY]: [theme.colors.blue[2]],
    };

    const filteredLeagues = leagueFilterData?.filter(
      ({ isEnabled, isActive }) => isEnabled && isActive
    );
    if (!filteredLeagues) return [];

    return filteredLeagues.map(({ value, label, Icon, sport }) => {
      // Shifting the color registry for each sport
      const color = SportColorMatrix[sport].shift();
      SportColorMatrix[sport].push(color);

      return (
        <UnstyledButtonLink
          key={value}
          className={classes.leagueCard}
          href={Routes.contestLobby({ [URLQueryKeys.LEAGUE]: value })}
          onClick={() =>
            reportEvent('Home > Leagues > Click', { league: label })
          }
        >
          <Icon style={{ stroke: color }} />
          <Text variant="subhead-medium">{label}</Text>
        </UnstyledButtonLink>
      );
    });
  }, [classes.leagueCard, leagueFilterData, theme.colors]);

  return (
    <div style={{ order }}>
      <HomeSectionTitle>{t('leagueLobbies.title')}</HomeSectionTitle>
      <div className={classes.container}>
        {isLoadingLeagueFilterData
          ? repeat(10, () => <Skeleton height={56} width={200} />)
          : LeagueCardsComponent}
      </div>
    </div>
  );
}

export default HomeLeagueLobbies;
